import DataSourceController from '@/views/fusion/investment/intelligence-create/controller/DataSourceController';
import store from '@/store';
/** 场景定向标签获取 */
import { geSceneSpecTagApi } from '@/api/tool/scene-spec-tag';
/** 流量包 */
import { getUnionPackageApi } from '@/api/tool/union-package';
/** 创意规格 */
import { getCreativeTemplateApi } from '@/api/creative';

/** 场景定投枚举 */
const SceneSpecTagEnums = Object.freeze({
  /** 微信公众号与小程序定投 */
  WECHAT_POSITION: 'WECHAT_POSITION',
  /** 微信视频号定投 */
  WECHAT_CHANNELS_SCENE: 'WECHAT_CHANNELS_SCENE',
  /** 微信公众号与小程序场景: 公众号媒体类型 */
  OFFICIAL_ACCOUNT_MEDIA_CATEGORY: 'OFFICIAL_ACCOUNT_MEDIA_CATEGORY',
  /** 微信公众号与小程序场景: 小程序小游戏流量类型 */
  MINI_PROGRAM_AND_MINI_GAME: 'MINI_PROGRAM_AND_MINI_GAME',
  /** 微信公众号与小程序场景: 订单详情页消费场景 */
  PAY_SCENE: 'PAY_SCENE',
  /** 优量汇媒体类型 */
  MOBILE_UNION_CATEGORY: 'MOBILE_UNION_CATEGORY',
});

/** 优汇量 */
const UNION_PACKAGE_TYPE = Object.freeze({
  /** 优量汇自定义定投 */
  UNION_PACKAGE_TYPE_INCLUDE: 'UNION_PACKAGE_TYPE_INCLUDE',
  /** 优量汇自定义屏蔽 */
  UNION_PACKAGE_TYPE_EXCLUDE: 'UNION_PACKAGE_TYPE_EXCLUDE',
});

/**
 * 管理广点通渠道的数据
 */
export default class GdtDataSourceController extends DataSourceController {
  /**
   * 根据类型获取优量汇流量包
   * @param {UNION_PACKAGE_TYPE} type 类型
   * @returns {Promise<Array>}
   */
  getUnionPackage(type){
    return new Promise((resolve) => {
      const filtering = { ...(type && { union_package_type: type }) };
      getUnionPackageApi({
        channel_interface: this.getChannelInterfaceEnum(),
        advertiser_id: this.getRequestAdvertiser(),
        filtering,
      })
        .then((res) => {
          const result = (res?.list || []).map(item => ({ label: item.name, value: item.id }));
          resolve(result);
        });
    });
  }
  /**
   * 请求场景定向标签
   * @param {Object} params 查询参数
   * @returns {Promise<Array>}
   */
  getSceneSpecTagList(params) {
    return new Promise((resolve) => {
      const { type, ...args } = params;
      const filtering = { ...(type && { type }) };
      geSceneSpecTagApi({
        advertiser_id: this.getRequestAdvertiser(),
        channel_interface: this.getChannelInterfaceEnum(),
        filtering,
        ...args,
      })
        .then((res) => {
          const result = (res?.list || []).map((item) => ({
            label: item.description,
            value: item.id,
          }));
          resolve(result);
        });
    });
  }
  /**
   * 获取微信公众号与小程序定投枚举
   * @returns {Promise<Array>}
   */
  async getWeChatPositionEnums() {
    try {
      const ret = await this.getSceneSpecTagList({ type: SceneSpecTagEnums.WECHAT_POSITION });
      return Promise.resolve(ret);
    } catch (error) {
      return Promise.reject(error);
    }
  }
  /**
   * 获取微信视频号定投枚举
   * @returns {Promise<Array>}
   */
  async getWeChannelsSceneEnums() {
    try {
      const ret = await this.getSceneSpecTagList({ type: SceneSpecTagEnums.WECHAT_CHANNELS_SCENE });
      return Promise.resolve(ret);
    } catch (error) {
      return Promise.reject(error);
    }
  }
  /**
   * 微信公众号与小程序场景: 公众号媒体类型
   * @returns {Promise<Array>}
   */
  async getOfficialAccountMediaCategoryEnums() {
    try {
      const ret = await this.getSceneSpecTagList({ type: SceneSpecTagEnums.OFFICIAL_ACCOUNT_MEDIA_CATEGORY });
      return Promise.resolve(ret);
    } catch (error) {
      return Promise.reject(error);
    }
  }
  /**
   * 微信公众号与小程序场景: 小程序小游戏流量类型
   * @returns {Promise<Array>}
   */
  async getMiniProgramAndMiniGameEnums() {
    try {
      const ret = await this.getSceneSpecTagList({ type: SceneSpecTagEnums.MINI_PROGRAM_AND_MINI_GAME });
      return Promise.resolve(ret);
    } catch (error) {
      return Promise.reject(error);
    }
  }
  /**
   * 微信公众号与小程序场景: 订单详情页消费场景
   * @returns {Promise<Array>}
   */
  async getPaySceneEnums() {
    try {
      const ret = await this.getSceneSpecTagList({ type: SceneSpecTagEnums.PAY_SCENE });
      return Promise.resolve(ret);
    } catch (error) {
      return Promise.reject(error);
    }
  }
  /**
   * 优量汇媒体类型
   * @returns {Promise<Array>}
   */
  async getMobileUnionCategoryEnums() {
    try {
      const ret = await this.getSceneSpecTagList({ type: SceneSpecTagEnums.MOBILE_UNION_CATEGORY });
      return Promise.resolve(ret);
    } catch (error) {
      return Promise.reject(error);
    }
  }
  /**
   * 优量汇自定义定投
   * @returns {Promise<Array>}
   */
  async getUnionPositionPackageEnums() {
    try {
      const ret = await this.getUnionPackage(UNION_PACKAGE_TYPE.UNION_PACKAGE_TYPE_INCLUDE);
      return Promise.resolve(ret);
    } catch (error) {
      return Promise.reject(error);
    }
  }
  /**
   * 优量汇自定义屏蔽
   * @returns {Promise<Array>}
   */
  async getExcludeUnionPositionPackageEnums() {
    try {
      const ret = await this.getUnionPackage(UNION_PACKAGE_TYPE.UNION_PACKAGE_TYPE_EXCLUDE);
      return Promise.resolve(ret);
    } catch (error) {
      return Promise.reject(error);
    }
  }
  /**
   * 获取创意规格列表
   * @param {Object} params 查询参数
   * @returns {Promise<Object[]>}
   */
  getCreativeTemplateList(params = {}) {
    return new Promise((resolve, reject) => {
      const payload = {
        channel_interface: this.getChannelInterfaceEnum(),
        advertiser_id: this.getRequestAdvertiser(),
        ...params,
      };
      getCreativeTemplateApi(payload).then(res => {
        const result = (res?.list || []).map(({ id, name }) => ({ label: name, value: id }));
        resolve(result);
      }).catch(reject);
    });
  }
  /**
   * 获取按钮文案app枚举
   * @returns {Promise<Object[]>}
   */
  getButtonTextAppEnums() {
    try {
      const { channel_interface } = this.config.init_ops;
      const data = channel_interface ? store.getters[`${channel_interface}Enum`].api_fusion_button_text_app : [];
      return Promise.resolve(data);
    } catch (error) {
      return Promise.reject(error);
    }
  }
  /**
   * 获取按钮文案小游戏枚举
   * @returns {Promise<Object[]>}
   */
  getButtonTextMgEnums() {
    try {
      const { channel_interface } = this.config.init_ops;
      const data = channel_interface ? store.getters[`${channel_interface}Enum`].api_fusion_button_text_mg : [];
      return Promise.resolve(data);
    } catch (error) {
      return Promise.reject(error);
    }
  }
  /**
   * 获取试玩页触发文案枚举
   * @returns {Promise<Object[]>}
   */
  getWxgamePlayablePageTriggerTextEnums() {
    try {
      const { channel_interface } = this.config.init_ops;
      const data = channel_interface ? store.getters[`${channel_interface}Enum`].api_fusion_wxgame_playable_page_trigger_text : [];
      return Promise.resolve(data);
    } catch (error) {
      return Promise.reject(error);
    }
  }
  /**
   * 获取学历枚举
   * @returns {Promise<Object[]>}
   */
  getEducationsEnums() {
    try {
      const { channel_interface } = this.config.init_ops;
      const data = channel_interface ? store.getters[`${channel_interface}Enum`].api_fusion_educations : [];
      return Promise.resolve(data);
    } catch (error) {
      return Promise.reject(error);
    }
  }
  /**
   * 获取婚恋育儿状态枚举
   * @returns {Promise<Object[]>}
   */
  getMaritalStatusEnums() {
    try {
      const { channel_interface } = this.config.init_ops;
      const data = channel_interface ? store.getters[`${channel_interface}Enum`].api_fusion_marital_status : [];
      return Promise.resolve(data);
    } catch (error) {
      return Promise.reject(error);
    }
  }
  /**
   * 获取游戏消费能力枚举
   * @returns {Promise<Object[]>}
   */
  getGameConsumptionLevelEnums() {
    try {
      const { channel_interface } = this.config.init_ops;
      const data = channel_interface ? store.getters[`${channel_interface}Enum`].api_fusion_game_consumption_level : [];
      return Promise.resolve(data);
    } catch (error) {
      return Promise.reject(error);
    }
  }
  /**
   * 获取排除已转化用户枚举
   * @returns {Promise<Object[]>}
   */
  getExcludedConvertedEnums() {
    try {
      const { channel_interface } = this.config.init_ops;
      const data = channel_interface ? store.getters[`${channel_interface}Enum`].api_fusion_excluded_converted : [];
      return Promise.resolve(data);
    } catch (error) {
      return Promise.reject(error);
    }
  }
  /**
   * 获取转化时间区间枚举
   * @returns {Promise<Object[]>}
   */
  getExcludedDayEnums() {
    try {
      const { channel_interface } = this.config.init_ops;
      const data = channel_interface ? store.getters[`${channel_interface}Enum`].api_fusion_excluded_day : [];
      return Promise.resolve(data);
    } catch (error) {
      return Promise.reject(error);
    }
  }
  /**
   * 获取操作系统版本枚举
   * @returns {Promise<Object[]>}
   */
  getOsVersionEnums() {
    try {
      const { channel_interface } = this.config.init_ops;
      const data = channel_interface
        ? [
          ...store.getters[`${channel_interface}Enum`].api_fusion_os_android,
          ...store.getters[`${channel_interface}Enum`].api_fusion_os_ios,
          ...store.getters[`${channel_interface}Enum`].api_fusion_os_other,
        ]
        : [];
      return Promise.resolve(data);
    } catch (error) {
      return Promise.reject(error);
    }
  }
  /**
   * 获取微信再营销枚举
   * @returns {Promise<Object[]>}
   */
  getWechatAdBehaviorEnums() {
    try {
      const { channel_interface } = this.config.init_ops;
      const data = channel_interface ? store.getters[`${channel_interface}Enum`].api_fusion_wechat_ad_behavior : [];
      const result = data.map(item => Object.assign(item, { disabled: false }));
      return Promise.resolve(result);
    } catch (error) {
      return Promise.reject(error);
    }
  }
}
